import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';
import { useTier } from '../hooks/useTier';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { db } from '../config/firebase';
import { doc, setDoc, serverTimestamp, collection, query, where, orderBy, getDocs, limit, updateDoc, deleteDoc } from 'firebase/firestore';
import StripeCheckout from '../components/StripeCheckout';
import PricingComparison from '../components/PricingComparison';
import WelcomeTutorial from '../components/WelcomeTutorial';
import FeatureTour from '../components/FeatureTour';
import ProfileOnboarding from '../components/ProfileOnboarding';
import { celebrateFirstMeeting } from '../utils/confetti';
import { CardSkeleton, HistorySkeleton } from '../components/Skeleton';
import MeetingTemplates from '../components/MeetingTemplates';
import CalendarIntegration from '../components/CalendarIntegration';
import HistoryDetailModal from '../components/HistoryDetailModal';
import RecordingsManager from '../components/RecordingsManager';
import NotesManager from '../components/NotesManager';
import UserProfile from '../components/UserProfile';
import UserSettings from '../components/UserSettings';
import MeetingStatsOverview from '../components/MeetingStatsOverview';
import { COPY_FEEDBACK_DURATION } from '../constants/timing';
import ScheduleMeetingModal from '../components/ScheduleMeetingModal';
import CreateOrganizationModal from '../components/organization/CreateOrganizationModal';
import OrganizationTabContent from '../components/OrganizationTabContent';
import { ChatSystem, ContactsSection } from '../components/chat';
import { useChat } from '../context/ChatContext';
import { trackPageView, trackUniqueVisitor } from '../services/analyticsService';
import WaitlistCard from '../components/WaitlistCard';
import OnboardingModal from '../components/OnboardingModal';

export default function Dashboard() {
  const { user, userProfile, logout } = useAuth();
  const { isPremium, tier, hasOrganization, organizationId, organizationRole } = useTier();
  const toast = useToast();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const { chats, pendingChats, pendingChatsCount, loading: chatsLoading, isChatOpen, selectChat, closeChat, acceptChat, declineChat, getChatDisplayName } = useChat();
  const [showChatSystem, setShowChatSystem] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [meetingId, setMeetingId] = useState('');
  const [chatRoomId, setChatRoomId] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedChat, setCopiedChat] = useState(false);
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [meetingName, setMeetingName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [useAccessCode, setUseAccessCode] = useState(false);
  const [enableWaitingRoom, setEnableWaitingRoom] = useState(false);
  const [saveChatHistory, setSaveChatHistory] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Loading states
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [isCreatingChatRoom, setIsCreatingChatRoom] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isGeneratingChatLink, setIsGeneratingChatLink] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [isJoiningChat, setIsJoiningChat] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState('meetings'); // meetings, chat, contacts, activity, organization
  const [activitySubsection, setActivitySubsection] = useState('history'); // history, notes, recordings
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);

  // Profile & Settings modal state
  const [showProfile, setShowProfile] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeatureTour, setShowFeatureTour] = useState(false);
  const [showProfileOnboarding, setShowProfileOnboarding] = useState(false);

  // Onboarding modal state (post-signup questionnaire)
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Schedule meeting state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [upcomingLoading, setUpcomingLoading] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyTimeFilter, setHistoryTimeFilter] = useState('all'); // all, today, week, month

  // New features state
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMeetingId, setCalendarMeetingId] = useState('');

  const lastMeetingCreationRef = useRef(0);

  // Simulate initial loading
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // Track page views and unique visitors
  useEffect(() => {
    trackPageView('dashboard');
    trackUniqueVisitor();
  }, []);

  // Check if user needs post-signup onboarding
  useEffect(() => {
    if (userProfile) {
      // Check if user has filled any optional data
      const hasOptionalData = userProfile.industry ||
                              userProfile.companySize ||
                              (userProfile.useCases && userProfile.useCases.length > 0);

      // User was "grandfathered" if onboardingCompleted is true but no data and no skip flag
      // This catches users from before onboarding was implemented
      const wasGrandfathered = userProfile.onboardingCompleted &&
                               !hasOptionalData &&
                               !userProfile.onboardingSkipped &&
                               !userProfile.onboardingCompletedAt;

      // Show modal if:
      // 1. Not completed and not skipped (normal flow), OR
      // 2. Was grandfathered (old user who never actually did onboarding)
      if ((!userProfile.onboardingCompleted && !userProfile.onboardingSkipped) || wasGrandfathered) {
        setShowOnboarding(true);
      }
    }
  }, [userProfile]);

  useEffect(() => {
    // Check for Stripe redirect parameters
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const upgrade = searchParams.get('upgrade');

    if (success === 'true') {
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 5000);
      // Clear query params
      searchParams.delete('success');
      setSearchParams(searchParams);
    }

    if (upgrade === 'true') {
      setShowCheckout(true);
      searchParams.delete('upgrade');
      setSearchParams(searchParams);
    }

    // Canceled is handled silently - just clear the param
    if (canceled === 'true') {
      searchParams.delete('canceled');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  // Load history from Firestore
  useEffect(() => {
    if (!user?.uid) return;

    const loadHistory = async () => {
      try {
        setHistoryLoading(true);
        const historyRef = collection(db, 'users', user.uid, 'meetingHistory');
        const q = query(historyRef, orderBy('createdAt', 'desc'), limit(20));
        const snapshot = await getDocs(q);

        const historyItems = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setHistory(historyItems);
      } catch (error) {
        // Permissions errors are common for new users without any history yet
        // or when Firestore rules haven't been deployed - gracefully degrade
        if (error.code === 'permission-denied') {
          console.warn('Unable to load meeting history - this is normal for new users or if Firestore rules need deployment');
        } else {
          console.error('Error loading history:', error);
        }
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [user]);

  // Load upcoming scheduled meetings
  useEffect(() => {
    if (!user?.uid) return;

    const loadUpcomingMeetings = async () => {
      try {
        setUpcomingLoading(true);
        const meetingsRef = collection(db, 'meetings');
        const now = new Date();

        // Query for scheduled meetings created by this user
        // Note: Removed orderBy to avoid requiring composite index
        // Sorting is done client-side instead (see below)
        const q = query(
          meetingsRef,
          where('createdBy', '==', user.uid),
          where('status', '==', 'scheduled')
        );

        const snapshot = await getDocs(q);

        const meetings = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            scheduledFor: doc.data().scheduledFor?.toDate?.() || new Date(doc.data().scheduledFor)
          }))
          .filter(meeting => meeting.scheduledFor > now) // Only future meetings
          .sort((a, b) => a.scheduledFor - b.scheduledFor); // Client-side sort (soonest first)

        setUpcomingMeetings(meetings);
      } catch (error) {
        if (error.code === 'permission-denied') {
          console.warn('Unable to load upcoming meetings - this is normal for new users');
        } else {
          console.error('Error loading upcoming meetings:', error);
        }
        setUpcomingMeetings([]);
      } finally {
        setUpcomingLoading(false);
      }
    };

    loadUpcomingMeetings();
  }, [user]);

  // Filter history by time period
  const getFilteredHistory = () => {
    if (historyTimeFilter === 'all') return history;

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    return history.filter(item => {
      const itemDate = item.createdAt?.toDate?.() || item.joinedAt?.toDate?.() || new Date(0);

      switch (historyTimeFilter) {
        case 'today':
          return itemDate >= todayStart;
        case 'week':
          return itemDate >= weekStart;
        case 'month':
          return itemDate >= monthStart;
        default:
          return true;
      }
    });
  };

  const createInstantMeeting = async () => {
    const now = Date.now();
    const timeSinceLastCreation = now - lastMeetingCreationRef.current;

    if (timeSinceLastCreation < 2000) {
      toast.warning('Please wait before creating another meeting');
      return;
    }

    lastMeetingCreationRef.current = now;

    setIsCreatingMeeting(true);
    try {
      // Generate 8-digit numeric meeting code
      const newMeetingId = Math.floor(10000000 + Math.random() * 90000000).toString();

      // Create meeting document in Firestore with iOS-compatible schema
      const meetingDoc = {
        id: newMeetingId,
        meetingCode: newMeetingId,  // iOS compatibility
        name: meetingName.trim() || `Meeting ${newMeetingId}`,
        createdBy: user.uid,
        createdByEmail: user.email,
        createdAt: serverTimestamp(),
        startedAt: serverTimestamp(),  // Track when meeting started
        status: 'active',  // iOS-compatible status field
        hasAccessCode: useAccessCode,
        accessCode: useAccessCode ? accessCode : null,
        waitingRoomEnabled: enableWaitingRoom,
        saveChatHistory: saveChatHistory,
        participants: [],  // Initialize empty participants array for iOS compatibility
        guestCount: 0
      };

      await setDoc(doc(db, 'meetings', newMeetingId), meetingDoc);

      // Check if this is the first meeting
      const isFirstMeeting = !localStorage.getItem('quickmeet_first_meeting_created');
      if (isFirstMeeting) {
        localStorage.setItem('quickmeet_first_meeting_created', 'true');
        setTimeout(() => celebrateFirstMeeting(), 500);
      }

      toast.success('Meeting created successfully!');

      // Show calendar integration modal
      setCalendarMeetingId(newMeetingId);
      setShowCalendar(true);
      setShowAccessCodeModal(false);
      setMeetingName('');
      setAccessCode('');
      setUseAccessCode(false);
      setEnableWaitingRoom(false);
      setSaveChatHistory(false);

      // Navigate after closing calendar modal (or immediately if user skips)
      // The calendar modal onClose will handle navigation
      // navigate(`/meet/${newMeetingId}`);
    } catch (error) {
      toast.error('Failed to create meeting. Please try again.');
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  const openAccessCodeModal = () => {
    setShowAccessCodeModal(true);
  };

  const joinMeeting = async (e) => {
    e.preventDefault();
    if (!meetingId.trim()) {
      toast.warning('Please enter a meeting code');
      return;
    }

    setIsJoining(true);
    try {
      // Navigate to meeting
      navigate(`/meet/${meetingId.trim()}`);
    } catch (error) {
      toast.error('Failed to join meeting. Please try again.');
      setIsJoining(false);
    }
  };

  const generateMeetingLink = async () => {
    setIsGeneratingLink(true);
    try {
      // Generate 8-digit numeric meeting code
      const newMeetingId = Math.floor(10000000 + Math.random() * 90000000).toString();

      await setDoc(doc(db, 'meetings', newMeetingId), {
        id: newMeetingId,
        meetingCode: newMeetingId,  // iOS compatibility
        name: meetingName.trim() || `Meeting ${newMeetingId}`,
        createdBy: user.uid,
        createdByEmail: user.email,
        createdAt: serverTimestamp(),
        startedAt: serverTimestamp(),  // Track when meeting started
        status: 'active',  // iOS-compatible status field
        hasAccessCode: useAccessCode,
        accessCode: useAccessCode ? accessCode : null,
        waitingRoomEnabled: enableWaitingRoom,
        saveChatHistory: saveChatHistory,
        participants: [],  // Initialize empty participants array for iOS compatibility
        guestCount: 0
      });

      const link = `${window.location.origin}/meet/${newMeetingId}`;
      await navigator.clipboard.writeText(link);
      setMeetingId(newMeetingId);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_FEEDBACK_DURATION);
      toast.success('Meeting link copied to clipboard!');
      setShowAccessCodeModal(false);
      setAccessCode('');
      setUseAccessCode(false);
      setEnableWaitingRoom(false);
      setSaveChatHistory(false);
    } catch (error) {
      toast.error('Failed to generate link. Please try again.');
    } finally {
      setIsGeneratingLink(false);
    }
  };

  // Chat room functions
  const createInstantChatRoom = async () => {
    setIsCreatingChatRoom(true);
    try {
      const newRoomId = uuidv4().slice(0, 8);

      // Create chat room document in Firestore
      const chatRoomDoc = {
        id: newRoomId,
        createdBy: user.uid,
        createdByEmail: user.email,
        createdAt: serverTimestamp(),
        hasAccessCode: useAccessCode,
        accessCode: useAccessCode ? accessCode : null,
        isActive: true
      };

      await setDoc(doc(db, 'chatRooms', newRoomId), chatRoomDoc);

      toast.success('Chat room created successfully!');
      navigate(`/chat/${newRoomId}`);
      setShowAccessCodeModal(false);
      setAccessCode('');
      setUseAccessCode(false);
    } catch (error) {
      toast.error('Failed to create chat room. Please try again.');
    } finally {
      setIsCreatingChatRoom(false);
    }
  };

  const joinChatRoom = async (e) => {
    e.preventDefault();
    if (!chatRoomId.trim()) {
      toast.warning('Please enter a chat room code');
      return;
    }

    setIsJoiningChat(true);
    try {
      navigate(`/chat/${chatRoomId.trim()}`);
    } catch (error) {
      toast.error('Failed to join chat room. Please try again.');
      setIsJoiningChat(false);
    }
  };

  const generateChatRoomLink = async () => {
    setIsGeneratingChatLink(true);
    try {
      const newRoomId = uuidv4().slice(0, 8);

      // Create chat room document
      await setDoc(doc(db, 'chatRooms', newRoomId), {
        id: newRoomId,
        createdBy: user.uid,
        createdByEmail: user.email,
        createdAt: serverTimestamp(),
        hasAccessCode: useAccessCode,
        accessCode: useAccessCode ? accessCode : null,
        isActive: true
      });

      const link = `${window.location.origin}/chat/${newRoomId}`;
      await navigator.clipboard.writeText(link);
      setChatRoomId(newRoomId);
      setCopiedChat(true);
      setTimeout(() => setCopiedChat(false), COPY_FEEDBACK_DURATION);
      toast.success('Chat room link copied to clipboard!');
      setShowAccessCodeModal(false);
      setAccessCode('');
      setUseAccessCode(false);
    } catch (error) {
      toast.error('Failed to generate link. Please try again.');
    } finally {
      setIsGeneratingChatLink(false);
    }
  };

  const deleteMeeting = async (meetingId) => {
    if (!confirm('Are you sure you want to delete this meeting?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'meetings', meetingId));
      toast.success('Meeting deleted successfully');

      // Reload upcoming meetings
      setUpcomingMeetings(prev => prev.filter(m => m.id !== meetingId));
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error('Failed to delete meeting');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#000000] transition-colors duration-300">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 animate-slide-down">
          <div className="bg-green-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <p className="font-semibold">Welcome to Premium!</p>
              <p className="text-sm text-white/90">Your subscription is now active</p>
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="border-b border-[#d2d2d7] dark:border-[#2c2c2e] bg-white/80 dark:bg-[#1c1c1e]/95 backdrop-blur-xl sticky top-0 z-50 animate-slide-down transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center">
          {/* Left: Logo */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1">
            <div className="w-9 h-9 flex items-center justify-center transform hover:scale-110 transition-all duration-300">
              <img
                src={isDark ? "/logo-white.png" : "/logo-blue.png"}
                alt="QuickMeet"
                className="w-9 h-9 object-contain"
              />
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <h1 className="text-lg sm:text-xl font-semibold text-[#1d1d1f] dark:text-white transition-colors">QuickMeet</h1>
                {tier && tier !== 'free' && (
                  <span className={`px-2 py-0.5 text-white text-[10px] font-semibold rounded-full ${
                    tier === 'team'
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700'
                      : tier === 'pro'
                      ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                      : 'bg-gradient-to-r from-gray-500 to-gray-600'
                  }`}>
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}
                  </span>
                )}
                {tier === 'free' && (
                  <span className="px-2 py-0.5 bg-gray-300 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-[10px] font-semibold rounded-full">
                    Free
                  </span>
                )}
              </div>
              <span className="text-[10px] sm:text-xs text-[#86868b] dark:text-[#8e8e93] -mt-1 transition-colors">by TheJhyeFactor</span>
            </div>
          </div>

          {/* Center: Upgrade Button */}
          <div className="flex-1 flex justify-center">
            {!isPremium && (
              <button
                onClick={() => setShowPricing(true)}
                className="px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white text-xs sm:text-sm font-medium rounded-full hover:shadow-lg transition-all hover:scale-105"
                data-tour="upgrade-button"
              >
                {(userProfile?.subscriptionTier || 'free').charAt(0).toUpperCase() + (userProfile?.subscriptionTier || 'free').slice(1)} - Upgrade Now
              </button>
            )}
          </div>

          {/* Right: Action Buttons */}
          <div className="flex items-center gap-3 sm:gap-4 flex-1 justify-end">
            {/* Profile Button */}
            <button
              onClick={() => setShowProfile(true)}
              className="p-2 rounded-full hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all duration-200"
              title="Profile"
            >
              <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>

            {/* Help/Tour Button */}
            <button
              onClick={() => setShowFeatureTour(true)}
              className="p-2 rounded-full hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all duration-200"
              title="Take a Tour"
            >
              <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>

            {/* Settings Button */}
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 rounded-full hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all duration-200"
              title="Settings"
              data-tour="settings-button"
            >
              <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            {/* Dark Mode Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all duration-200"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? (
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {userProfile?.isAdmin === true && (
              <button
                onClick={() => navigate('/admin')}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-all hover:scale-105 flex items-center gap-1"
                title="Admin Dashboard"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span className="hidden sm:inline">Admin</span>
              </button>
            )}

            <button
              onClick={logout}
              className="text-sm text-[#06c] hover:text-[#0077ed] font-medium transition-all duration-200 hover:scale-105"
            >
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-8 sm:pb-12 text-center animate-fade-in">
        <h2 className="text-4xl sm:text-6xl md:text-7xl font-semibold text-[#1d1d1f] dark:text-white mb-4 sm:mb-6 tracking-tight animate-slide-up transition-colors">
          Video & Chat.
          <br />
          For everyone.
        </h2>
        <p className="text-lg sm:text-2xl text-[#6e6e73] dark:text-[#8e8e93] font-normal max-w-2xl mx-auto leading-relaxed animate-slide-up-delay transition-colors">
          Connect instantly with video meetings or secure chat rooms. No downloads. No limits.
          Just pure, simple communication.
        </p>
      </div>

      {/* iOS Waitlist Card */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <WaitlistCard />
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-8">
        <div className="flex items-center gap-1 sm:gap-2 bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-2xl p-1 transition-colors overflow-x-auto">
          <button
            onClick={() => setActiveTab('meetings')}
            className={`flex-1 px-2 sm:px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
              activeTab === 'meetings'
                ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
            }`}
          >
            Meetings
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 px-2 sm:px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
              activeTab === 'chat'
                ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
            }`}
          >
            Chat
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`flex-1 px-2 sm:px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
              activeTab === 'contacts'
                ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
            }`}
          >
            Contacts
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`flex-1 px-2 sm:px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
              activeTab === 'activity'
                ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
            }`}
            data-tour="activity-tab"
          >
            Activity
          </button>
          {tier === 'team' && (
            <button
              onClick={() => setActiveTab('organization')}
              className={`flex-1 px-2 sm:px-4 py-2.5 rounded-xl font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${
                activeTab === 'organization'
                  ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                  : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
              }`}
            >
              Organization
            </button>
          )}
        </div>
      </div>

      {/* Main Actions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 sm:pb-20">
        {activeTab === 'meetings' && (
          <>
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* Start Meeting Card */}
          <div className="bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed] rounded-3xl p-6 sm:p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group animate-card-1" onClick={openAccessCodeModal} data-tour="new-meeting">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-black to-[#2d2d2d] rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3 group-hover:text-[#06c] transition-colors">
              New Meeting
            </h3>
            <p className="text-sm sm:text-base text-[#6e6e73] mb-4 sm:mb-6 leading-relaxed">
              Start an instant video meeting. Share the link and connect with anyone, anywhere.
            </p>
            <button
              disabled={isCreatingMeeting}
              className="bg-black text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-medium hover:bg-[#06c] hover:shadow-lg transform hover:scale-105 transition-all duration-200 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {isCreatingMeeting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                'Start Meeting'
              )}
            </button>
          </div>

          {/* Join Meeting Card */}
          <div className="bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed] rounded-3xl p-6 sm:p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 animate-card-2" data-tour="join-meeting">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-2xl flex items-center justify-center mb-4 sm:mb-6 hover:scale-110 hover:rotate-3 transition-all duration-300 shadow-lg">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-[#1d1d1f] mb-2 sm:mb-3">
              Join Meeting
            </h3>
            <p className="text-sm sm:text-base text-[#6e6e73] mb-4 sm:mb-6 leading-relaxed">
              Enter a meeting code to join an existing room.
            </p>
            <form onSubmit={joinMeeting} className="space-y-3">
              <input
                type="text"
                value={meetingId}
                onChange={(e) => setMeetingId(e.target.value)}
                placeholder="Enter meeting code"
                className="w-full px-4 py-2.5 sm:py-3 bg-white border border-[#d2d2d7] rounded-xl text-sm sm:text-base text-[#1d1d1f] placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all hover:border-[#06c]"
              />
              <button
                type="submit"
                disabled={isJoining}
                className="w-full bg-gradient-to-r from-[#06c] to-[#0055d4] text-white py-2.5 sm:py-3 rounded-full font-medium hover:shadow-lg transform hover:scale-105 transition-all duration-200 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
              >
                {isJoining ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Joining...
                  </>
                ) : (
                  'Join'
                )}
              </button>
            </form>
          </div>
        </div>
          )}

        {/* Generate Link Section */}
        {!isLoading && (
        <div className="bg-white dark:bg-[#1c1c1e] border border-[#d2d2d7] dark:border-[#2c2c2e] rounded-3xl p-8 mb-6 sm:mb-8 transition-colors">
          <div className="flex items-start gap-6">
            <div className="w-12 h-12 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors">
              <svg className="w-6 h-6 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-2 transition-colors">
                Get a link to share
              </h3>
              <p className="text-[#6e6e73] dark:text-[#8e8e93] mb-4 leading-relaxed transition-colors">
                Generate a meeting link you can share with others. They can join anytime.
              </p>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={generateMeetingLink}
                  disabled={isGeneratingLink}
                  className="bg-black dark:bg-white text-white dark:text-black px-6 py-2.5 rounded-full font-medium hover:bg-[#424245] dark:hover:bg-[#e8e8ed] transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isGeneratingLink ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white dark:border-black border-t-transparent rounded-full animate-spin"></div>
                      Generating...
                    </>
                  ) : copied ? '✓ Link Copied' : 'Generate Link'}
                </button>

                <button
                  onClick={() => setShowTemplates(true)}
                  className="bg-[#06c] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#0077ed] transition-all text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Use Template
                </button>

                <button
                  onClick={() => setShowScheduleModal(true)}
                  className="bg-purple-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-purple-700 transition-all text-sm flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule Meeting
                </button>

              </div>
              {meetingId && (
                <div className="mt-4 p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl transition-colors">
                  <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mb-1 transition-colors">Meeting Code</p>
                  <p className="font-mono text-lg font-semibold text-[#1d1d1f] dark:text-white transition-colors">{meetingId}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Personal Meeting Room Section */}
        {!isLoading && (
          <div className="bg-gradient-to-br from-[#06c]/10 to-[#0055d4]/10 dark:from-[#06c]/20 dark:to-[#0055d4]/20 border border-[#06c]/20 dark:border-[#06c]/30 rounded-3xl p-8 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-12 h-12 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-2xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-2">Your Personal Meeting Room</h3>
                  <p className="text-[#6e6e73] dark:text-[#8e8e93] mb-4 leading-relaxed">
                    Your permanent meeting link. Share it with anyone for instant access.
                  </p>
                  <div className="bg-white dark:bg-[#2c2c2e] rounded-xl p-4 border border-[#d2d2d7] dark:border-[#3a3a3c]">
                    <p className="text-xs text-[#86868b] dark:text-[#8e8e93] mb-1">Personal Room URL</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-sm text-[#06c] dark:text-[#0088cc] break-all flex-1">
                        {window.location.origin}/meet/personal-{user.uid.slice(0, 8)}
                      </p>
                      <button
                        onClick={async () => {
                          await navigator.clipboard.writeText(`${window.location.origin}/meet/personal-${user.uid.slice(0, 8)}`);
                          toast.success('Personal room link copied!');
                        }}
                        className="p-2 hover:bg-[#f5f5f7] dark:hover:bg-[#3a3a3c] rounded-lg transition-all flex-shrink-0"
                        title="Copy link"
                      >
                        <svg className="w-5 h-5 text-[#06c] dark:text-[#0088cc]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <button
                      onClick={() => navigate(`/meet/personal-${user.uid.slice(0, 8)}`)}
                      className="bg-gradient-to-r from-[#06c] to-[#0055d4] text-white px-6 py-2.5 rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all text-sm"
                    >
                      Enter Room
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Meetings Section */}
        {!isLoading && upcomingMeetings.length > 0 && (
          <div className="bg-white dark:bg-[#1c1c1e] border border-[#d2d2d7] dark:border-[#2c2c2e] rounded-3xl p-8 transition-colors">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/20 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">Upcoming Meetings</h3>
                  <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">{upcomingMeetings.length} scheduled</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {upcomingMeetings.slice(0, 5).map((meeting) => {
                const formatDateTime = (date) => {
                  if (!date) return 'N/A';
                  return date.toLocaleString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  });
                };

                const timeUntil = (date) => {
                  if (!date) return '';
                  const now = new Date();
                  const diff = date - now;
                  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                  if (days > 0) return `in ${days}d ${hours}h`;
                  if (hours > 0) return `in ${hours}h`;
                  return 'soon';
                };

                return (
                  <div
                    key={meeting.id}
                    className="bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl p-4 hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => navigate(`/meet/${meeting.id}`)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-[#1d1d1f] dark:text-white font-semibold truncate group-hover:text-purple-600 transition-colors">
                            {meeting.name}
                          </h4>
                          <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs rounded-lg whitespace-nowrap">
                            {timeUntil(meeting.scheduledFor)}
                          </span>
                        </div>
                        <p className="text-[#86868b] dark:text-[#8e8e93] text-sm mb-2">
                          {formatDateTime(meeting.scheduledFor)}
                        </p>
                        {meeting.description && (
                          <p className="text-[#6e6e73] dark:text-[#8e8e93] text-sm line-clamp-1">
                            {meeting.description}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className="px-2 py-1 bg-[#e8e8ed] dark:bg-[#3a3a3c] text-[#1d1d1f] dark:text-white text-xs rounded-lg">
                            {meeting.duration} min
                          </span>
                          {meeting.hasAccessCode && (
                            <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs rounded-lg">
                              Access Code Required
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMeeting(meeting.id);
                          }}
                          className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                          title="Delete meeting"
                        >
                          <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {upcomingMeetings.length > 5 && (
              <div className="mt-4 text-center">
                <p className="text-sm text-[#86868b] dark:text-[#8e8e93]">
                  +{upcomingMeetings.length - 5} more upcoming meetings
                </p>
              </div>
            )}
          </div>
        )}
        </>
        )}

        {activeTab === 'chat' && (
          <div className="max-w-4xl mx-auto">
            {/* Messages Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white transition-colors">
                  Chat
                </h2>
                <p className="text-[#86868b] dark:text-[#8e8e93] text-sm mt-1 transition-colors">
                  Your conversations
                </p>
              </div>
              <button
                onClick={() => setShowChatSystem(true)}
                className="bg-gradient-to-r from-[#06c] to-[#0088cc] text-white px-5 py-2.5 rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all text-sm flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Message
              </button>
            </div>

            {/* Pending Message Requests */}
            {pendingChatsCount > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800/30 rounded-3xl overflow-hidden mb-4 transition-colors">
                <div className="p-4 border-b border-blue-200/50 dark:border-blue-800/30">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white">Message Requests</h3>
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-full">
                      {pendingChatsCount}
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-blue-200/50 dark:divide-blue-800/30">
                  {pendingChats.slice(0, 3).map(chat => (
                    <div key={chat.id} className="p-4 flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-semibold text-lg">
                          {getChatDisplayName(chat).charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1d1d1f] dark:text-white truncate">
                          {getChatDisplayName(chat)}
                        </p>
                        <p className="text-sm text-[#86868b] dark:text-[#8e8e93]">Wants to message you</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => acceptChat(chat.id)}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-full transition-colors"
                        >
                          Accept
                        </button>
                        <button
                          onClick={() => declineChat(chat.id)}
                          className="px-4 py-2 bg-[#f5f5f7] dark:bg-[#2c2c2e] hover:bg-[#e8e8ed] dark:hover:bg-[#3c3c3e] text-[#1d1d1f] dark:text-white text-sm font-medium rounded-full transition-colors"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                  {pendingChatsCount > 3 && (
                    <button
                      onClick={() => setShowChatSystem(true)}
                      className="w-full p-3 text-center text-blue-600 dark:text-blue-400 font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                    >
                      View all {pendingChatsCount} requests
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Recent Conversations */}
            <div className="bg-white dark:bg-[#1c1c1e] border border-[#d2d2d7] dark:border-[#2c2c2e] rounded-3xl overflow-hidden transition-colors">
              {chatsLoading ? (
                <div className="p-8 space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-4 animate-pulse">
                      <div className="w-12 h-12 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-full" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded w-1/3" />
                        <div className="h-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : chats.length === 0 && pendingChatsCount === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-16 h-16 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                    <svg className="w-8 h-8 text-[#86868b] dark:text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2 transition-colors">No conversations</h3>
                  <p className="text-[#86868b] dark:text-[#8e8e93] mb-6 transition-colors">Start a conversation to begin chatting</p>
                  <button
                    onClick={() => setShowChatSystem(true)}
                    className="bg-gradient-to-r from-[#06c] to-[#0088cc] text-white px-6 py-3 rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all"
                  >
                    Start a Conversation
                  </button>
                </div>
              ) : chats.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-[#86868b] dark:text-[#8e8e93]">No active conversations</p>
                </div>
              ) : (
                <div className="divide-y divide-[#d2d2d7] dark:divide-[#2c2c2e]">
                  {chats.slice(0, 5).map(chat => (
                    <button
                      key={chat.id}
                      onClick={() => {
                        selectChat(chat.id);
                        setShowChatSystem(true);
                      }}
                      className="w-full p-4 flex items-center gap-4 hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-colors text-left"
                    >
                      {/* Avatar */}
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        chat.type === 'dm' ? 'bg-gradient-to-br from-[#06c] to-[#0088cc]' :
                        chat.type === 'group' ? 'bg-gradient-to-br from-purple-500 to-purple-700' :
                        chat.type === 'org_channel' ? 'bg-gradient-to-br from-green-500 to-green-700' :
                        'bg-gradient-to-br from-orange-500 to-orange-700'
                      }`}>
                        {chat.type === 'dm' ? (
                          <span className="text-white font-semibold text-lg">
                            {chat.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        ) : chat.type === 'org_channel' ? (
                          <span className="text-white font-semibold text-lg">#</span>
                        ) : (
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        )}
                      </div>

                      {/* Chat Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium truncate transition-colors ${chat.hasUnread ? 'text-[#1d1d1f] dark:text-white' : 'text-[#1d1d1f] dark:text-white/80'}`}>
                            {chat.type === 'org_channel' ? `#${chat.channelSlug || chat.name}` : chat.name || 'Unnamed Chat'}
                          </span>
                        </div>
                        <p className={`text-sm truncate transition-colors ${chat.hasUnread ? 'text-[#1d1d1f] dark:text-white/70 font-medium' : 'text-[#86868b] dark:text-[#8e8e93]'}`}>
                          {chat.lastMessage?.text || 'No messages yet'}
                        </p>
                      </div>

                      {/* Unread badge & Timestamp */}
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className="text-xs text-[#86868b] dark:text-[#8e8e93] transition-colors">
                          {chat.lastMessage?.timestamp ? (
                            new Date(chat.lastMessage.timestamp.toDate?.() || chat.lastMessage.timestamp).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric'
                            })
                          ) : ''}
                        </span>
                        {chat.hasUnread && (
                          <span className="min-w-[20px] h-5 px-1.5 bg-[#06c] text-white text-xs font-bold rounded-full flex items-center justify-center">
                            1
                          </span>
                        )}
                      </div>
                    </button>
                  ))}

                  {/* View All Button */}
                  {chats.length > 5 && (
                    <button
                      onClick={() => setShowChatSystem(true)}
                      className="w-full p-4 text-center text-[#06c] dark:text-[#0088cc] font-medium hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-colors"
                    >
                      View all {chats.length} conversations
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Quick Access Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {/* Legacy P2P Chat Room Card */}
              <div className="bg-gradient-to-br from-[#e8f4f8] to-[#d4e9f0] dark:from-[#0c2a3a] dark:to-[#0a1f2c] rounded-2xl p-6 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#06c] to-[#0088cc] rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-1 transition-colors">P2P Chat Room</h3>
                    <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93] mb-3 transition-colors">Temporary encrypted rooms</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={createInstantChatRoom}
                        disabled={isCreatingChatRoom}
                        className="text-sm px-4 py-2 bg-white dark:bg-[#2c2c2e] text-[#06c] dark:text-[#0088cc] rounded-full font-medium hover:shadow-md transition-all disabled:opacity-50"
                      >
                        {isCreatingChatRoom ? 'Creating...' : 'Create Room'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Open Full Chat */}
              <div
                onClick={() => setShowChatSystem(true)}
                className="bg-gradient-to-br from-[#f0e8f8] to-[#e0d4f0] dark:from-[#2a0c3a] dark:to-[#1f0a2c] rounded-2xl p-6 cursor-pointer hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-1 transition-colors">Full Messages View</h3>
                    <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93] mb-3 transition-colors">Open all your conversations</p>
                    <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                      Open Messages →
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="max-w-4xl mx-auto">
            <ContactsSection />
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="max-w-6xl mx-auto">
            {/* Activity Subsection Navigation */}
            <div className="mb-8">
              <div className="flex items-center gap-2 bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-2xl p-1 transition-colors">
                <button
                  onClick={() => setActivitySubsection('history')}
                  className={`flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                    activitySubsection === 'history'
                      ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                      : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>History</span>
                  </div>
                </button>
                <button
                  onClick={() => setActivitySubsection('notes')}
                  className={`flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                    activitySubsection === 'notes'
                      ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                      : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Notes</span>
                  </div>
                </button>
                <button
                  onClick={() => setActivitySubsection('recordings')}
                  className={`flex-1 px-4 py-2.5 rounded-xl font-medium text-sm transition-all ${
                    activitySubsection === 'recordings'
                      ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                      : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Recordings</span>
                  </div>
                </button>
              </div>
            </div>

            {/* History Subsection */}
            {activitySubsection === 'history' && (
          <div className="max-w-6xl mx-auto">
            {historyLoading ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <HistorySkeleton />
                  <HistorySkeleton />
                  <HistorySkeleton />
                </div>
                <div className="space-y-3">
                  <HistorySkeleton />
                  <HistorySkeleton />
                  <HistorySkeleton />
                </div>
              </div>
            ) : history.length === 0 ? (
              <div className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-3xl p-12 text-center transition-colors">
                <div className="w-16 h-16 bg-[#d2d2d7] dark:bg-[#2c2c2e] rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                  <svg className="w-8 h-8 text-[#86868b] dark:text-[#8e8e93]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-[#86868b] dark:text-[#8e8e93] text-lg transition-colors">No meeting history yet</p>
                <p className="text-[#86868b] dark:text-[#8e8e93] text-sm mt-2 transition-colors">Your past meetings will appear here</p>
              </div>
            ) : (
              <>
                {/* Stats Overview */}
                <MeetingStatsOverview history={history} />

                {/* Time Filter Buttons */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-2xl p-1 transition-colors overflow-x-auto">
                    <button
                      onClick={() => setHistoryTimeFilter('all')}
                      className={`px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                        historyTimeFilter === 'all'
                          ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                          : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
                      }`}
                    >
                      All Time
                    </button>
                    <button
                      onClick={() => setHistoryTimeFilter('today')}
                      className={`px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                        historyTimeFilter === 'today'
                          ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                          : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      onClick={() => setHistoryTimeFilter('week')}
                      className={`px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                        historyTimeFilter === 'week'
                          ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                          : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
                      }`}
                    >
                      This Week
                    </button>
                    <button
                      onClick={() => setHistoryTimeFilter('month')}
                      className={`px-4 py-2 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
                        historyTimeFilter === 'month'
                          ? 'bg-white dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white shadow-md'
                          : 'text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white'
                      }`}
                    >
                      This Month
                    </button>
                  </div>
                </div>

                {/* Meeting History List */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white transition-colors">
                      {historyTimeFilter === 'today' ? "Today's Meetings" :
                       historyTimeFilter === 'week' ? "This Week's Meetings" :
                       historyTimeFilter === 'month' ? "This Month's Meetings" :
                       "Recent Meetings"}
                    </h3>
                    <span className="text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">
                      {getFilteredHistory().length} {getFilteredHistory().length === 1 ? 'meeting' : 'meetings'}
                    </span>
                  </div>
                </div>
              <div className="space-y-3">
                {getFilteredHistory().map((item) => {
                  const formatDate = (timestamp) => {
                    if (!timestamp) return 'N/A';
                    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
                    return date.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    });
                  };

                  const formatDuration = (seconds) => {
                    if (!seconds) return null;
                    const hours = Math.floor(seconds / 3600);
                    const minutes = Math.floor((seconds % 3600) / 60);
                    if (hours > 0) {
                      return `${hours}h ${minutes}m`;
                    } else if (minutes > 0) {
                      return `${minutes}m`;
                    }
                    return `${seconds}s`;
                  };

                  return (
                    <div
                      key={item.id}
                      className="bg-[#f5f5f7] dark:bg-[#1c1c1e] rounded-2xl p-4 hover:shadow-lg transition-all cursor-pointer group border border-transparent dark:border-[#2c2c2e]"
                      onClick={() => setSelectedHistory(item)}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          item.type === 'video'
                            ? 'bg-gradient-to-br from-black to-[#2d2d2d]'
                            : 'bg-gradient-to-br from-[#06c] to-[#0088cc]'
                        }`}>
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {item.type === 'video' ? (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            )}
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="text-[#1d1d1f] dark:text-white font-semibold group-hover:text-[#06c] transition-colors truncate">
                              {item.meetingId}
                            </h4>
                            <svg className="w-5 h-5 text-[#86868b] dark:text-[#8e8e93] group-hover:text-[#06c] group-hover:translate-x-1 transition-all flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                          <p className="text-[#86868b] dark:text-[#8e8e93] text-sm transition-colors mb-2">
                            {formatDate(item.createdAt)}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {item.duration !== undefined && formatDuration(item.duration) && (
                              <span className="px-2 py-1 bg-[#e8e8ed] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-xs rounded-lg">
                                {formatDuration(item.duration)}
                              </span>
                            )}
                            {item.recordingId && (
                              <span className="px-2 py-1 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs rounded-lg flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                  <circle cx="12" cy="12" r="8" />
                                </svg>
                                Recorded
                              </span>
                            )}
                            {item.chatSaved && (
                              <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs rounded-lg">
                                Chat Saved
                              </span>
                            )}
                            {item.participantCount !== undefined && (
                              <span className="px-2 py-1 bg-[#e8e8ed] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-xs rounded-lg">
                                {item.participantCount} {item.participantCount === 1 ? 'participant' : 'participants'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              </>
            )}
          </div>
            )}

            {/* Notes Subsection */}
            {activitySubsection === 'notes' && (
              <NotesManager />
            )}

            {/* Recordings Subsection */}
            {activitySubsection === 'recordings' && (
              <RecordingsManager />
            )}
          </div>
        )}
      </div>

      {/* Features Section - Hidden on Organization Tab */}
      {activeTab !== 'organization' && (
        <div className="bg-[#f5f5f7] dark:bg-[#1c1c1e] py-20 transition-colors">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-white dark:bg-[#2c2c2e] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm transition-colors">
                  <svg className="w-8 h-8 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2 transition-colors">
                  Private & Secure
                </h4>
                <p className="text-[#6e6e73] dark:text-[#8e8e93] leading-relaxed transition-colors">
                  End-to-end encrypted peer-to-peer connections keep your conversations private.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white dark:bg-[#2c2c2e] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm transition-colors">
                  <svg className="w-8 h-8 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2 transition-colors">
                  Lightning Fast
                </h4>
                <p className="text-[#6e6e73] dark:text-[#8e8e93] leading-relaxed transition-colors">
                  Start meetings instantly. No downloads, no waiting, no hassle.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white dark:bg-[#2c2c2e] rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm transition-colors">
                  <svg className="w-8 h-8 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-[#1d1d1f] dark:text-white mb-2 transition-colors">
                  Always Free
                </h4>
                <p className="text-[#6e6e73] dark:text-[#8e8e93] leading-relaxed transition-colors">
                  Unlimited meetings, unlimited time. No hidden costs, ever.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Tab */}
      {activeTab === 'organization' && tier === 'team' && (
        <OrganizationTabContent
          hasOrganization={hasOrganization}
          organizationId={organizationId}
          organizationRole={organizationRole}
          userProfile={userProfile}
          showCreateOrgModal={showCreateOrgModal}
          setShowCreateOrgModal={setShowCreateOrgModal}
        />
      )}

      {/* Footer */}
      <div className="border-t border-[#d2d2d7] dark:border-[#2c2c2e] py-8 bg-[#f5f5f7] dark:bg-[#1c1c1e] transition-colors">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-lg flex items-center justify-center shadow-md">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-[#1d1d1f] dark:text-white transition-colors">QuickMeet</span>
                <span className="text-xs text-[#86868b] dark:text-[#8e8e93] transition-colors">by TheJhyeFactor</span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 text-xs sm:text-sm text-[#86868b] dark:text-[#8e8e93] transition-colors">
              <Link to="/privacy" className="hover:text-[#06c] transition-colors">Privacy Policy</Link>
              <span>·</span>
              <Link to="/terms" className="hover:text-[#06c] transition-colors">Terms of Service</Link>
              <span>·</span>
              <span className="text-[#1d1d1f] dark:text-white font-medium transition-colors">© 2025 TheJhyeFactor</span>
            </div>
          </div>
        </div>
      </div>

      {/* Access Code Modal */}
      {showAccessCodeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1c1c1e] rounded-3xl shadow-2xl max-w-md w-full p-8 animate-fade-in transition-colors">
            <h3 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white mb-4 transition-colors">Create Secure Meeting</h3>
            <p className="text-[#6e6e73] dark:text-[#8e8e93] mb-6 transition-colors">Customize your meeting settings and security options.</p>

            {/* Meeting Name Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2 transition-colors">
                Meeting Name <span className="text-[#6e6e73] dark:text-[#8e8e93] font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                value={meetingName}
                onChange={(e) => setMeetingName(e.target.value)}
                placeholder="e.g., Team Standup, Client Review"
                maxLength="50"
                className="w-full px-4 py-3.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:bg-white dark:focus:bg-[#3a3a3c] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all"
              />
              <p className="text-xs text-[#6e6e73] dark:text-[#8e8e93] mt-2 transition-colors">
                {meetingName.trim() ? `Recording will be saved as "${meetingName.trim()}"` : 'Auto-generated name will be used if left empty'}
              </p>
            </div>

            {/* Use Access Code Toggle */}
            <div className="mb-6">
              <label className="flex items-center justify-between cursor-pointer p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all">
                <div>
                  <p className="font-medium text-[#1d1d1f] dark:text-white transition-colors">Require Access Code</p>
                  <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93] transition-colors">Only people with the code can join</p>
                </div>
                <input
                  type="checkbox"
                  checked={useAccessCode}
                  onChange={(e) => setUseAccessCode(e.target.checked)}
                  className="w-12 h-6 rounded-full appearance-none bg-[#d2d2d7] relative cursor-pointer transition-colors checked:bg-[#06c] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-2"
                  style={{
                    backgroundImage: useAccessCode
                      ? 'radial-gradient(circle at 70% 50%, white 40%, transparent 40%)'
                      : 'radial-gradient(circle at 30% 50%, white 40%, transparent 40%)'
                  }}
                />
              </label>
            </div>

            {/* Access Code Input */}
            {useAccessCode && (
              <div className="mb-6 animate-slide-up">
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2 transition-colors">
                  Access Code
                </label>
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  className="w-full px-4 py-3.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-transparent rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:bg-white dark:focus:bg-[#3a3a3c] focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all"
                />
                <p className="text-xs text-[#6e6e73] dark:text-[#8e8e93] mt-2 transition-colors">Share this code with meeting participants</p>
              </div>
            )}

            {/* Waiting Room Toggle */}
            <div className="mb-6">
              <label className="flex items-center justify-between cursor-pointer p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all">
                <div>
                  <p className="font-medium text-[#1d1d1f] dark:text-white transition-colors">Enable Waiting Room</p>
                  <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93] transition-colors">Review and admit participants before they join</p>
                </div>
                <input
                  type="checkbox"
                  checked={enableWaitingRoom}
                  onChange={(e) => setEnableWaitingRoom(e.target.checked)}
                  className="w-12 h-6 rounded-full appearance-none bg-[#d2d2d7] relative cursor-pointer transition-colors checked:bg-[#06c] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-2"
                  style={{
                    backgroundImage: enableWaitingRoom
                      ? 'radial-gradient(circle at 70% 50%, white 40%, transparent 40%)'
                      : 'radial-gradient(circle at 30% 50%, white 40%, transparent 40%)'
                  }}
                />
              </label>
            </div>

            {/* Save Chat History Toggle */}
            <div className="mb-6">
              <label className="flex items-center justify-between cursor-pointer p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-2xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all">
                <div>
                  <p className="font-medium text-[#1d1d1f] dark:text-white transition-colors">Save Chat History</p>
                  <p className="text-sm text-[#6e6e73] dark:text-[#8e8e93] transition-colors">Chat messages will be saved and viewable after the meeting</p>
                </div>
                <input
                  type="checkbox"
                  checked={saveChatHistory}
                  onChange={(e) => setSaveChatHistory(e.target.checked)}
                  className="w-12 h-6 rounded-full appearance-none bg-[#d2d2d7] relative cursor-pointer transition-colors checked:bg-[#06c] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-2"
                  style={{
                    backgroundImage: saveChatHistory
                      ? 'radial-gradient(circle at 70% 50%, white 40%, transparent 40%)'
                      : 'radial-gradient(circle at 30% 50%, white 40%, transparent 40%)'
                  }}
                />
              </label>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAccessCodeModal(false);
                  setMeetingName('');
                  setAccessCode('');
                  setUseAccessCode(false);
                  setEnableWaitingRoom(false);
                  setSaveChatHistory(false);
                }}
                className="flex-1 px-4 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all"
              >
                Cancel
              </button>
              <button
                onClick={createInstantMeeting}
                disabled={useAccessCode && accessCode.length !== 6}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-xl font-medium hover:shadow-xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                Create Meeting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Comparison Modal */}
      {showPricing && (
        <PricingComparison
          onClose={() => setShowPricing(false)}
          onSelectPlan={(planId) => {
            if (planId === 'pro') {
              setShowPricing(false);
              setShowCheckout(true);
            }
          }}
        />
      )}

      {/* Stripe Checkout Modal */}
      {showCheckout && <StripeCheckout onClose={() => setShowCheckout(false)} />}

      {/* Calendar Integration Modal */}
      {showCalendar && calendarMeetingId && (
        <CalendarIntegration
          meetingId={calendarMeetingId}
          onClose={() => {
            setShowCalendar(false);
            navigate(`/meet/${calendarMeetingId}`);
          }}
        />
      )}

      {/* Meeting Templates Modal */}
      {showTemplates && (
        <MeetingTemplates
          onSelectTemplate={async (template) => {
            setShowTemplates(false);

            try {
              // Generate 8-digit numeric meeting code
              const newMeetingId = Math.floor(10000000 + Math.random() * 90000000).toString();

              await setDoc(doc(db, 'meetings', newMeetingId), {
                id: newMeetingId,
                meetingCode: newMeetingId,  // iOS compatibility
                name: template.name,
                createdBy: user.uid,
                createdByEmail: user.email,
                createdAt: serverTimestamp(),
                startedAt: serverTimestamp(),  // Track when meeting started
                status: 'active',  // iOS-compatible status field
                hasAccessCode: useAccessCode,
                accessCode: useAccessCode ? accessCode : null,
                waitingRoomEnabled: enableWaitingRoom,
                saveChatHistory: saveChatHistory,
                participants: [],  // Initialize empty participants array for iOS compatibility
                guestCount: 0,
                duration: template.duration,
                template: {
                  id: template.id,
                  name: template.name,
                  icon: template.icon,
                  duration: template.duration,
                  agenda: template.agenda,
                  tips: template.tips
                }
              });

              toast.success(`Created ${template.name} meeting!`);

              // Navigate to the meeting
              navigate(`/meet/${newMeetingId}`);
            } catch (error) {
              toast.error('Failed to create meeting. Please try again.');
            }
          }}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Welcome Tutorial */}
      <WelcomeTutorial />

      {/* Feature Tour */}
      <FeatureTour isOpen={showFeatureTour} onClose={() => setShowFeatureTour(false)} />

      {/* Profile Onboarding */}
      {showProfileOnboarding && (
        <ProfileOnboarding onComplete={() => setShowProfileOnboarding(false)} />
      )}

      {/* Post-Signup Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />

      {/* History Detail Modal */}
      {selectedHistory && (
        <HistoryDetailModal
          historyItem={selectedHistory}
          onClose={() => setSelectedHistory(null)}
        />
      )}

      {/* Profile Modal */}
      {showProfile && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setShowProfile(false)}
        >
          <div
            className="w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setShowProfile(false)}
                className="absolute -top-3 -right-3 z-10 p-2 bg-white dark:bg-[#2c2c2e] rounded-full shadow-lg hover:bg-[#f5f5f7] dark:hover:bg-[#3c3c3e] transition"
              >
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <UserProfile />
            </div>
          </div>
        </div>
      )}

      {/* Schedule Meeting Modal */}
      {showScheduleModal && (
        <ScheduleMeetingModal
          onClose={() => setShowScheduleModal(false)}
          onSuccess={(meetingId) => {
            // Reload upcoming meetings
            const loadUpcomingMeetings = async () => {
              try {
                setUpcomingLoading(true);
                const meetingsRef = collection(db, 'meetings');
                const now = new Date();
                // Removed orderBy to avoid composite index requirement
                const q = query(
                  meetingsRef,
                  where('createdBy', '==', user.uid),
                  where('status', '==', 'scheduled')
                );
                const snapshot = await getDocs(q);
                const meetings = snapshot.docs
                  .map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    scheduledFor: doc.data().scheduledFor?.toDate?.() || new Date(doc.data().scheduledFor)
                  }))
                  .filter(meeting => meeting.scheduledFor > now)
                  .sort((a, b) => a.scheduledFor - b.scheduledFor); // Client-side sort
                setUpcomingMeetings(meetings);
              } catch (error) {
                console.error('Error reloading meetings:', error);
              } finally {
                setUpcomingLoading(false);
              }
            };
            loadUpcomingMeetings();
          }}
        />
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center p-4 z-50"
          onClick={() => setShowSettings(false)}
        >
          <div
            className="w-full max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button
                onClick={() => setShowSettings(false)}
                className="absolute -top-3 -right-3 z-10 p-2 bg-white dark:bg-[#2c2c2e] rounded-full shadow-lg hover:bg-[#f5f5f7] dark:hover:bg-[#3c3c3e] transition"
              >
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <UserSettings />
            </div>
          </div>
        </div>
      )}

      {/* Floating Chat Button */}
      <button
        onClick={() => setShowChatSystem(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-[#06c] to-[#0055d4] rounded-full shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center z-40 group"
        title="Open Messages"
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        {/* Unread indicator dot - only show if there are unread messages */}
        {chats.some(c => c.hasUnread) && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white dark:border-[#1c1c1e]" />
        )}
      </button>

      {/* Chat System Overlay */}
      <ChatSystem
        isOpen={showChatSystem}
        onClose={() => setShowChatSystem(false)}
      />
    </div>
  );
}
